import { useGrowthBook } from '@growthbook/growthbook-react';
import { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { TranslationsFunc, useTranslations } from '@lang';
import { usePaymentTermsLabel } from '@common/hooks/usePaymentTermsLabel';
import { useRates, useRatePageSettings } from '@features/rates';
import { RatesFilterType } from '@features/rates/types';
import { useRoomPageSettings, useStaticRooms } from '@features/rooms';
import { getLargestRoomOccupancy } from '@features/rooms/helpers/getLargestRoomOccupancy.helper';
import { useGetPackagesAvailability } from '@features/packages/hooks/useGetPackagesAvailability';
import { hasPackageToCheck } from '@features/packages/utils/hasPackageToCheck';
import { usePackagesSpaAvailability } from '@features/spa/hooks/useGetPackagesSpaAvailability';
import { parseDateToString } from '@utils/helpers';
import { mapDiscountToDisplayObject } from '@utils/mapDiscountToDisplayObject';
import { queryParamToString } from '@utils/queryParam';
import { AccommodationListItem } from 'voyager';
import { useRouter } from 'shared-routing';
import { Property } from 'shared-types/Property';
import { Rate } from '@features/rates/types';
import {
  getPackageInclusions,
  inclusionLabelKeys,
  getRateInclusions,
  PackageInclusion,
  packageExtraInclusions,
} from '@features/packages/utils/getPackageInclusions';
import { getRateQuickView } from '@features/rates/helpers/getRateQuickView.helper';
import { fitsPackageStayLength } from '@features/packages/utils/fitsPackageStayLength';
import moment from 'moment';

export const LISTING_FULL_PACKAGE_CHECK_FLAG = 'feature.listing-full-package-check';

// Past this time a package shows as when its check fails, so one slow spa answer
// cannot keep the whole listing behind the spinner.
export const LISTING_PACKAGE_CHECK_TIMEOUT_MS = 10000;

export const sortUnavailableLast = <T>(items: T[], isUnavailable: (item: T) => boolean): T[] =>
  [...items].sort((a, b) => Number(isUnavailable(a)) - Number(isUnavailable(b)));

export const markPackagesOutsideStayLength = (rates: Rate[], stayNights: number): Rate[] =>
  sortUnavailableLast(
    rates.map((rate) =>
      fitsPackageStayLength(rate.package, stayNights) ? rate : { ...rate, unAvailable: true }
    ),
    (rate) => !!rate.unAvailable
  );

const inclusionSignature = (item: AccommodationListItem) => (item.inclusions ?? []).join('|');

export const getOfferedInclusions = (items: AccommodationListItem[]): PackageInclusion[] => {
  if (new Set(items.map(inclusionSignature)).size < 2) return [];

  const offered = new Set(items.flatMap((item) => item.inclusions ?? []));

  return packageExtraInclusions.filter((inclusion) => offered.has(inclusion));
};

export const mapRateToListItem = (
  rate: Rate,
  t: TranslationsFunc,
  fallbackImageUrl?: string,
  packageAvailability?: { isUnavailable: boolean; isCheckFailed?: boolean },
  priceNote?: string | null,
  stayNights = 1
): AccommodationListItem => {
  const isPackageSoldOut =
    !!packageAvailability?.isUnavailable && !packageAvailability.isCheckFailed;
  const unavailable = rate.unAvailable || isPackageSoldOut;
  const discount = unavailable ? undefined : rate.discount;

  return {
    id: rate.id,
    name: rate.title,
    imageUrl: rate.imageUrl || fallbackImageUrl || undefined,
    price:
      rate.price && !unavailable
        ? { amount: rate.price.amount, currencyCode: rate.price.currencyCode }
        : undefined,
    discount: mapDiscountToDisplayObject(discount),
    promotionName: discount?.name,
    priceNote: priceNote ?? undefined,
    badges: getPackageInclusions(rate.package).map((inclusion) => t(inclusionLabelKeys[inclusion])),
    inclusions: getRateInclusions(rate.package),
    unavailable,
    unavailableLabel: t('common.unavailable'),
    quickView: getRateQuickView(rate, t, stayNights),
  };
};

export function useRatesList(
  journeyParams: ParsedJourneyParams,
  language: string,
  property: Property,
  type?: RatesFilterType
) {
  const { t } = useTranslations();
  const router = useRouter();
  const getPaymentTermsLabel = usePaymentTermsLabel();

  const isSpaStays = type === RatesFilterType.SPA_STAYS;

  const { content: ratesContent, isFetching: isFetchingRates } = useRatePageSettings(
    language,
    t('common.rate')
  );
  const { content: roomsContent, isFetching: isFetchingRooms } = useRoomPageSettings(
    language,
    t('common.rate')
  );

  const promoRateCode =
    queryParamToString(router.query.promoRateCode) ?? journeyParams.promoRateCode;

  const adults = journeyParams.adults ?? 0;
  const children = journeyParams.children ?? 0;
  const rooms = journeyParams.rooms || [{ roomNumber: 1, adults, children }];

  const {
    rates: availableRates,
    isFetching: isFetchingRateData,
    error,
    isAvailabilityUnknown,
    isRetryingAvailability,
    retryAvailability,
  } = useRates({
    fromDate: parseDateToString(journeyParams.fromDate),
    toDate: parseDateToString(journeyParams.toDate),
    adults,
    children,
    promoRateCode,
    childAges: journeyParams.childAges,
    rooms,
    type,
    language,
    availabilityMode: 'listing',
  });

  const { data: staticRooms, isFetching: isFetchingStaticRooms } = useStaticRooms();
  const largestRoomOccupancy = getLargestRoomOccupancy(staticRooms?.data ?? []);
  const parties = rooms.length > 1 ? rooms : [{ adults, children }];
  const isPartyTooLarge =
    largestRoomOccupancy !== null &&
    availableRates.every((rate) => rate.unAvailable) &&
    parties.some((party) => party.adults + party.children > largestRoomOccupancy);

  const stayNights = moment(journeyParams.toDate).diff(moment(journeyParams.fromDate), 'days');
  const rates = markPackagesOutsideStayLength(availableRates, stayNights);

  // A package is only sellable when each part of it can still be booked, which the
  // rate availability response does not know about. The flag picks the detail page's
  // full check or the spa time band check, and is read only for listings with a package
  // the rates still offer, so that only those guests enter the experiment.
  const packageRates = rates.filter((rate) => !rate.unAvailable && hasPackageToCheck(rate.package));
  const growthbook = useGrowthBook();
  const isFullPackageCheck =
    packageRates.length > 0 && !!growthbook?.isOn(LISTING_FULL_PACKAGE_CHECK_FLAG);
  const canCheckPackages = !!property?.id && !isFetchingRateData;

  const { data: packagesAvailability, isFetching: isFetchingPackagesAvailability } =
    useGetPackagesAvailability(
      property,
      packageRates,
      journeyParams,
      isFullPackageCheck && canCheckPackages,
      LISTING_PACKAGE_CHECK_TIMEOUT_MS
    );
  const { data: spaAvailability, isFetching: isFetchingSpaAvailability } =
    usePackagesSpaAvailability(
      property,
      rates,
      journeyParams,
      !isFullPackageCheck && isSpaStays && canCheckPackages
    );
  const packageAvailabilityByRateId = isFullPackageCheck
    ? new Map(
        (packagesAvailability ?? []).flatMap((availability) =>
          availability ? [[availability.rate.id, availability] as const] : []
        )
      )
    : new Map(Object.entries(spaAvailability ?? {}));

  const fallbackImageUrl =
    ratesContent?.heroBackgroundImage ?? roomsContent?.heroBackgroundImage ?? undefined;

  const mappedItems: AccommodationListItem[] = rates.map((rate) =>
    mapRateToListItem(
      rate,
      t,
      fallbackImageUrl ?? undefined,
      packageAvailabilityByRateId.get(rate.id) ?? undefined,
      getPaymentTermsLabel(rate.paymentTerms),
      stayNights
    )
  );
  // The control arm keeps the release order, where a package the spa check rules out stays in place.
  const listItems = isFullPackageCheck
    ? sortUnavailableLast(mappedItems, (item) => !!item.unavailable)
    : mappedItems;
  const packageChecksTimedOut = isFullPackageCheck
    ? (packagesAvailability ?? []).filter((availability) => availability?.isTimedOut).length
    : 0;

  const inclusionOptions = getOfferedInclusions(listItems).map((inclusion) => ({
    value: inclusion,
    label: t(inclusionLabelKeys[inclusion]),
  }));

  return {
    listItems,
    inclusionOptions,
    content: ratesContent,
    fallbackTitle: roomsContent?.title,
    isFetching:
      isFetchingRateData ||
      isFetchingRates ||
      isFetchingRooms ||
      isFetchingStaticRooms ||
      isFetchingPackagesAvailability ||
      isFetchingSpaAvailability,
    error,
    isAvailabilityUnknown,
    isPartyTooLarge,
    largestRoomOccupancy,
    isRetrying: isRetryingAvailability,
    retry: retryAvailability,
    packageCount: packageRates.length,
    packageChecksTimedOut,
    isFullPackageCheck,
  };
}
